import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FormikProvider, useFormik } from 'formik';
import { AlignLeft, CheckSquare, Circle, Hash, Link2, List, Mail, Plus, Type } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { paths } from 'src/config/paths';
import { useProjectsContext } from 'src/modules/projects/context';
import { FormPageTemplate } from 'src/templates/FormPageTemplate/FormPageTemplate';
import * as Yup from 'yup';

import { Callout } from '@/components/callouts/Callout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  Switch,
  Text,
  Tooltip,
} from '@/design-system/components/ui';

import {
  AffiliateApplicationFormField,
  AffiliateApplicationFormFieldType,
  PutFormConfigDTO,
} from '../../domain/affiliate-application-types';
import { useAffiliateApplicationFormConfig } from '../../hooks/useAffiliateApplicationFormConfig';
import { useEnableFormConfig } from '../../hooks/useEnableFormConfig';
import { useSaveFormConfig } from '../../hooks/useSaveFormConfig';
import { FormFieldItem } from './FormFieldItem';

const formBuilderSchema = Yup.object({
  formFields: Yup.array()
    .of(
      Yup.object({
        id: Yup.string().required(),
        label: Yup.string().required('Label is required'),
        type: Yup.string()
          .oneOf(['text', 'email', 'textarea', 'number', 'select', 'checkbox', 'radio', 'url'])
          .required('Type is required'),
        required: Yup.boolean().required(),
        placeholder: Yup.string(),
        description: Yup.string(),
        options: Yup.array()
          .of(Yup.string())
          .when('type', {
            is: (type: string) => type === 'select' || type === 'radio',
            then: (schema) => schema.min(1, 'At least one option is required'),
          }),
        validation: Yup.object({
          minLength: Yup.number().nullable().min(0, 'Must be 0 or greater'),
          maxLength: Yup.number().nullable().min(1, 'Must be 1 or greater'),
          min: Yup.number().nullable(),
          max: Yup.number().nullable(),
          pattern: Yup.string(),
        }),
      }),
    )
    .min(1, 'At least one field is required'),
});

export interface FormFieldValues {
  id: string;
  label: string;
  type: AffiliateApplicationFormFieldType;
  required: boolean;
  placeholder: string;
  description: string;
  options: string[];
  validation: {
    minLength: number | null;
    maxLength: number | null;
    min: number | null;
    max: number | null;
    pattern: string;
  };
}

interface FormBuilderValues {
  formFields: FormFieldValues[];
}

const mapFieldToFormValues = (field: AffiliateApplicationFormField): FormFieldValues => ({
  id: field.id,
  label: field.label,
  type: field.type,
  required: field.required,
  placeholder: field.placeholder ?? '',
  description: field.description ?? '',
  options: field.options ?? [],
  validation: {
    minLength: field.validation?.minLength ?? null,
    maxLength: field.validation?.maxLength ?? null,
    min: field.validation?.min ?? null,
    max: field.validation?.max ?? null,
    pattern: field.validation?.pattern ?? '',
  },
});

const hasValidation = (field: FormFieldValues): boolean => {
  const v = field.validation;
  return v.minLength !== null || v.maxLength !== null || v.min !== null || v.max !== null || v.pattern !== '';
};

const buildValidation = (field: FormFieldValues) => {
  const v = field.validation;
  return {
    ...(v.minLength !== null ? { minLength: v.minLength } : {}),
    ...(v.maxLength !== null ? { maxLength: v.maxLength } : {}),
    ...(v.min !== null ? { min: v.min } : {}),
    ...(v.max !== null ? { max: v.max } : {}),
    ...(v.pattern !== '' ? { pattern: v.pattern } : {}),
  };
};

export const FormBuilderView = () => {
  const router = useRouter();
  const slug = router.query.slug as string;
  const { project } = useProjectsContext();

  const { formConfig, isLoading } = useAffiliateApplicationFormConfig({
    projectId: project?.id || '',
  });

  const { saveFormConfig, isLoading: isSaving } = useSaveFormConfig();
  const { enableFormConfig, isLoading: isEnabling } = useEnableFormConfig();
  const [expandedField, setExpandedField] = useState<string>('');
  const [clickMenuPos, setClickMenuPos] = useState<{ x: number; y: number } | null>(null);
  const clickMenuRef = useRef<HTMLDivElement>(null);

  const handleAddFieldClick = useCallback((e: React.MouseEvent) => {
    setClickMenuPos({ x: e.clientX, y: e.clientY });
  }, []);

  useEffect(() => {
    if (!clickMenuPos) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (clickMenuRef.current && !clickMenuRef.current.contains(e.target as Node)) {
        setClickMenuPos(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clickMenuPos]);

  const initialValues: FormBuilderValues = useMemo(
    () => ({
      formFields: formConfig
        ? formConfig.formFields
            .slice()
            .sort((a, b) => a.order - b.order)
            .map(mapFieldToFormValues)
        : [
            {
              id: crypto.randomUUID(),
              label: '',
              type: 'text' as AffiliateApplicationFormFieldType,
              required: false,
              placeholder: '',
              description: '',
              options: [],
              validation: { minLength: null, maxLength: null, min: null, max: null, pattern: '' },
            },
          ],
    }),

    [formConfig?.id, formConfig?.updatedAt],
  );

  const formik = useFormik<FormBuilderValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema: formBuilderSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (formik.dirty) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formik.dirty]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = () => {
    setExpandedField('');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = formik.values.formFields.findIndex((f) => f.id === active.id);
      const newIndex = formik.values.formFields.findIndex((f) => f.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        formik.setFieldValue('formFields', arrayMove(formik.values.formFields, oldIndex, newIndex));
      }
    }
  };

  const handleAddField = (type: AffiliateApplicationFormFieldType) => {
    const newField: FormFieldValues = {
      id: crypto.randomUUID(),
      label: '',
      type,
      required: false,
      placeholder: '',
      description: '',
      options: type === 'select' || type === 'radio' ? [''] : [],
      validation: { minLength: null, maxLength: null, min: null, max: null, pattern: '' },
    };
    formik.setFieldValue('formFields', [...formik.values.formFields, newField]);
  };

  const fieldTypeOptions: { type: AffiliateApplicationFormFieldType; label: string; icon: React.ReactNode }[] = [
    { type: 'text', label: 'Text', icon: <Type size={16} aria-hidden="true" /> },
    { type: 'email', label: 'Email', icon: <Mail size={16} aria-hidden="true" /> },
    { type: 'textarea', label: 'Textarea', icon: <AlignLeft size={16} aria-hidden="true" /> },
    { type: 'number', label: 'Number', icon: <Hash size={16} aria-hidden="true" /> },
    { type: 'select', label: 'Select', icon: <List size={16} aria-hidden="true" /> },
    { type: 'radio', label: 'Radio', icon: <Circle size={16} aria-hidden="true" /> },
    { type: 'checkbox', label: 'Checkbox', icon: <CheckSquare size={16} aria-hidden="true" /> },
    { type: 'url', label: 'URL', icon: <Link2 size={16} aria-hidden="true" /> },
  ];

  const handleDeleteField = (fieldId: string) => {
    formik.setFieldValue(
      'formFields',
      formik.values.formFields.filter((f) => f.id !== fieldId),
    );
  };

  const handleSave = async () => {
    const errors = await formik.validateForm();
    formik.setTouched({
      formFields: formik.values.formFields.map(() => ({
        label: true,
        type: true,
        options: true,
      })),
    } as Parameters<typeof formik.setTouched>[0]);

    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the form errors before saving');
      return;
    }

    const dto: PutFormConfigDTO = {
      form_fields: formik.values.formFields.map((field, index) => ({
        id: field.id,
        label: field.label,
        type: field.type,
        required: field.required,
        order: index + 1,
        ...(field.placeholder ? { placeholder: field.placeholder } : {}),
        ...(field.description ? { description: field.description } : {}),
        ...((field.type === 'select' || field.type === 'radio') && field.options.length > 0
          ? { options: field.options }
          : {}),
        ...(hasValidation(field) ? { validation: buildValidation(field) } : {}),
      })),
    };
    saveFormConfig({ projectId: project?.id || '', dto });
  };

  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);

  const handleToggleEnabled = (checked: boolean) => {
    if (!project?.id) return;
    if (!checked) {
      setDisableDialogOpen(true);
      return;
    }
    enableFormConfig({ projectId: project.id, enabled: true });
  };

  const handleConfirmDisable = () => {
    if (!project?.id) return;
    enableFormConfig({ projectId: project.id, enabled: false });
    setDisableDialogOpen(false);
  };

  return (
    <FormikProvider value={formik}>
      <FormPageTemplate
        title="Form Builder"
        backButton={{
          text: 'Back to Affiliate Applications',
          href: paths.project.affiliateManagement.applications(slug),
          onClick: formik.dirty
            ? (e: React.MouseEvent) => {
                e.preventDefault();
                setUnsavedDialogOpen(true);
              }
            : undefined,
        }}
        isLoading={isLoading}
        headerActions={
          <Tooltip content="When disabled, the application form will not be available and all new applications will be rejected.">
            <div className="tw:flex tw:items-center tw:gap-2">
              <Badge variant={formConfig?.enabled ? 'success' : 'error'}>
                <span
                  className={
                    formConfig?.enabled
                      ? 'tw:w-2 tw:h-2 tw:rounded-full tw:bg-green-500'
                      : 'tw:w-2 tw:h-2 tw:rounded-full tw:bg-red-500'
                  }
                />
                {formConfig?.enabled ? 'Form Enabled' : 'Form Disabled'}
              </Badge>
              <Switch
                checked={formConfig?.enabled ?? false}
                onCheckedChange={handleToggleEnabled}
                disabled={!formConfig || isEnabling}
              />
            </div>
          </Tooltip>
        }
        actions={
          <>
            <div />
            <Button variant="primary" onClick={handleSave} isLoading={isSaving} disabled={!formik.dirty}>
              Save
            </Button>
          </>
        }
      >
        {isLoading ? (
          <div className="tw:flex tw:flex-col tw:gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" width="100%" height={64} />
            ))}
          </div>
        ) : (
          <>
            {formConfig && !formConfig.enabled && (
              <Callout
                type="warning"
                title="Form is disabled"
                body="This form is currently not receiving applications. Enable the form using the toggle above to start accepting affiliate applications."
              />
            )}

            {/* Section header */}
            <div className="tw:flex tw:items-center tw:justify-between">
              <Text variant="title-sm" weight="semibold">
                Configure Questions
              </Text>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="tw:text-primary">
                    <Plus size={16} aria-hidden="true" />
                    Add Field
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {fieldTypeOptions.map((opt) => (
                    <DropdownMenuItem key={opt.type} onClick={() => handleAddField(opt.type)}>
                      {opt.icon}
                      {opt.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={formik.values.formFields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                <div className="tw:flex tw:flex-col tw:gap-3">
                  {formik.values.formFields.map((field, index) => (
                    <FormFieldItem
                      key={field.id}
                      field={field}
                      index={index}
                      expanded={expandedField}
                      onExpand={setExpandedField}
                      onDelete={() => handleDeleteField(field.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            <Button variant="outline" className="tw:w-full tw:border-dashed" onClick={handleAddFieldClick}>
              <Plus size={16} aria-hidden="true" />
              Add Field
            </Button>

            {clickMenuPos && (
              <div
                ref={clickMenuRef}
                className="tw:fixed tw:z-[1600] tw:min-w-[200px] tw:overflow-hidden tw:rounded-[10px] tw:border tw:border-border tw:bg-background tw:p-1 tw:text-foreground tw:shadow-[0px_0px_20px_0px_rgba(19,16,30,0.1)] tw:animate-in tw:fade-in-0 tw:zoom-in-95"
                style={{ top: clickMenuPos.y, left: clickMenuPos.x, transform: 'translate(-50%, -100%)' }}
              >
                {fieldTypeOptions.map((opt) => (
                  <button
                    key={opt.type}
                    type="button"
                    className="tw:relative tw:flex tw:w-full tw:cursor-default tw:select-none tw:items-center tw:gap-2 tw:rounded-sm tw:px-3 tw:py-2 tw:text-sm tw:font-normal tw:outline-none tw:transition-colors tw:hover:bg-muted"
                    onClick={() => {
                      handleAddField(opt.type);
                      setClickMenuPos(null);
                    }}
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {formik.values.formFields.length === 0 && (
              <div className="tw:text-center tw:py-8">
                <Text variant="body-md" color="muted">
                  No fields yet. Click &quot;Add Field&quot; to start building your form.
                </Text>
              </div>
            )}

            {typeof formik.errors.formFields === 'string' && formik.touched.formFields && (
              <Text variant="body-sm" className="tw:text-destructive tw:text-center">
                {formik.errors.formFields}
              </Text>
            )}
          </>
        )}
      </FormPageTemplate>

      <AlertDialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Application Form</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disable the application form? The form will no longer be available and all new
              applications will be rejected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline">Cancel</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button variant="destructive" onClick={handleConfirmDisable}>
                Disable Form
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={unsavedDialogOpen} onOpenChange={setUnsavedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes that will be lost if you leave this page. Are you sure you want to go back?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline">Stay</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                variant="destructive"
                onClick={() => router.push(paths.project.affiliateManagement.applications(slug))}
              >
                Leave
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormikProvider>
  );
};
